<script setup lang="ts">
import { UK_TOOLS, type UkTool } from '~/constants/ukTools'

/**
 * A short "for UK applicants" band on an English page that is close to the
 * Civil Service or NHS writers in subject (the interview and cover letter
 * guides, the tailoring page, a healthcare or office resume example): one
 * line of why, and a plain link to each writer it names. Nothing in Czech
 * or German, where those pages do not exist.
 */
const props = defineProps<{
  /** Why a UK applicant reading this page needs the writer, in a sentence or two. */
  text: string
  /** Which writers to link; both by default. */
  tools?: UkTool['id'][]
  heading?: string
}>()

const { locale } = useI18n()
const shown = computed(() => (locale.value === 'en' ? UK_TOOLS.filter(tool => !props.tools || props.tools.includes(tool.id)) : []))
</script>

<template>
  <section v-if="shown.length" class="sofax bg-white dark:bg-gray-950">
    <div class="shell py-10 md:py-14">
      <div class="mx-auto max-w-4xl rounded-[0.9375rem] border border-line bg-surface p-6 sm:p-8 dark:border-gray-700 dark:bg-gray-800">
        <div class="text-xs font-semibold uppercase tracking-wide text-accent-strong dark:text-accent-soft">For UK applicants</div>
        <h2 class="mt-2 !text-2xl">{{ props.heading ?? 'Applying for a public-sector job in the UK?' }}</h2>
        <p class="mt-3 !text-base">{{ props.text }}</p>
        <ul class="mt-5 grid gap-3" :class="shown.length > 1 ? 'sm:grid-cols-2' : ''">
          <li v-for="tool in shown" :key="tool.id">
            <NuxtLink
              :to="tool.path"
              class="group flex h-full items-start gap-3 rounded-[0.625rem] bg-white p-4 transition-shadow duration-300 hover:shadow-lift dark:bg-gray-900"
            >
              <i class="bi mt-0.5 shrink-0 text-xl text-accent" :class="tool.icon" aria-hidden="true" />
              <span class="min-w-0">
                <span class="block font-semibold !text-ink group-hover:!text-accent dark:!text-white">{{ tool.title }} →</span>
                <span class="mt-1 block !text-sm !text-muted dark:!text-gray-400">{{ tool.description }}</span>
              </span>
            </NuxtLink>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>
